import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs/internal/Observable";
import { environment } from "../../../environments/environment";
import { GenericResponseDto } from "../models/generic-response.dto";
import { VendorDto } from "../models/vendor/addVendor/vendorDto";
import { AddressFormats } from "../models/vendor/addVendor/addressFormat";
import { GetVendorAccountNumberResponse } from "../models/vendor/addVendor/getVendorAccountNumberResponseDto";
import { VendorGroup } from "../models/vendor/addVendor/vendorGroup";
import { AddVendorAddressRequestDto } from "../models/vendor/summary/addVendorAddressRequestDto";
import { AddVendorAddressResponseDto } from "../models/vendor/summary/addVendorAddressResponseDto";
import { OpenPOLinesResponseDto } from "../models/vendor/summary/openPOLinesResponseDto";
import { OpenPOResponseDto } from "../models/vendor/summary/openPOResponseDto";
import { BillingRepresentative } from "../models/vendor/summary/billingRepresntative";
import { VendorFeedback } from "../models/vendor/summary/vendorFeedback";
import { VendorFeedbackDto } from "../models/vendor/summary/vendorFeedbackDto";
import { VendorFeedbackRequestDto } from "../models/vendor/summary/vendorFeedbackRequestDto";
import { VendorSummaryDto } from "../models/vendor/summary/vendorSummaryDto";
import { VendorTransactionCountDto } from "../models/vendor/summary/vendorTransactionCountDto";
import { GetAllVendorsDto } from "../models/vendor/vendorListing/getAllVendors";
import { AddCustomerContactInformationResponseDto } from "../models/client/addclient/AddCustomerContactInformationResponseDto";
import { AddContactsDto } from "../models/client/addclient/addContactsDto";
import { UpdateCustomerContactInformationResponseDto } from "../models/client/addclient/UpdateCustomerContactInformationResponseDto";
import { CreateAttachmentResponseDto } from "../models/shared/createAttachmentResponseDto";
import { CreateAttachmentRequestDto } from "../models/shared/createAttachmentRequestDto";
import { Vendor } from "../models/vendor/addVendor/vendor";
import { AddVendorDto } from "../models/vendor/addVendor/addVendorDto";
import { VendorAttachments } from "../models/vendor/addVendor/vendorAttachment";
import { ClientAttachment } from "../models/client/details/clientAttachment";
import { GenericRequestDto } from "../models/common/genericRequestDto";

@Injectable({
  providedIn: "root",
})
export class VendorService {
  private d365SynchroniserBaseURL = environment.baseUrl + "/D365Synchroniser";
  private vendorManagementBaseURL = environment.baseUrl + "/Vendor";
  private commonBaseURL = environment.baseUrl + "/Common";
  private locationBaseURL = environment.baseUrl + "/Location";
  constructor(private _http: HttpClient) {}

  addVendorData(
    payload: AddVendorDto
  ): Observable<GenericResponseDto<VendorDto>> {
    return this._http.post<GenericResponseDto<VendorDto>>(
      this.vendorManagementBaseURL + "/AddVendor",
      payload
    );
  }

  getVendorAccount(): Observable<
    GenericResponseDto<GetVendorAccountNumberResponse>
  > {
    return this._http.get<GenericResponseDto<GetVendorAccountNumberResponse>>(
      this.vendorManagementBaseURL + "/GetVendorAccountNumber"
    );
  }

  getVendorGroupList(): Observable<GenericResponseDto<VendorGroup[]>> {
    return this._http.get<GenericResponseDto<VendorGroup[]>>(
      this.vendorManagementBaseURL + "/GetAllVendorGroup"
    );
  }

  getVendorOnNameFilter(
    name = "",
    taxid = ""
  ): Observable<GenericResponseDto<GetAllVendorsDto[]>> {
    return this._http.get<GenericResponseDto<GetAllVendorsDto[]>>(
      this.vendorManagementBaseURL +
        `/GetSearchVendors?vendorName=${name}&taxId=${taxid}`
    );
  }

  getAddressFormatsByFormatId(
    formatId: string
  ): Observable<GenericResponseDto<AddressFormats>> {
    return this._http.get<GenericResponseDto<AddressFormats>>(
      `${this.locationBaseURL}/GetAddressFormatsByFormatId?formatId=${formatId}`
    );
  }

  getAllVendor(
    payload: GenericRequestDto
  ): Observable<GenericResponseDto<GetAllVendorsDto[]>> {
    return this._http.post<GenericResponseDto<GetAllVendorsDto[]>>(
      this.vendorManagementBaseURL + `/GetAllVendors`,
      payload
    );
  }

  getVendorInformation(
    accountnumber: string
  ): Observable<GenericResponseDto<VendorSummaryDto>> {
    return this._http.get<GenericResponseDto<VendorSummaryDto>>(
      this.vendorManagementBaseURL +
        `/GetVendorByAccountNumber?AccountNumber=${accountnumber}`
    );
  }
  getVendorFeedback(
    accountNumber: string,
    partyNumber: string | undefined
  ): Observable<GenericResponseDto<VendorFeedbackDto>> {
    return this._http.get<GenericResponseDto<VendorFeedbackDto>>(
      this.vendorManagementBaseURL +
        `/GetVendorFeedback?accountNumber=${accountNumber}&partyNumber=${partyNumber}`
    );
  }
  AddVendorFeedback(
    payload: VendorFeedbackRequestDto[]
  ): Observable<GenericResponseDto<VendorFeedback[]>> {
    return this._http.post<GenericResponseDto<VendorFeedback[]>>(
      this.vendorManagementBaseURL + "/AddVendorFeedbacks",
      payload
    );
  }
  uploadW9FileOnSummary(
    payload: ClientAttachment
  ): Observable<GenericResponseDto<VendorAttachments>> {
    return this._http.post<GenericResponseDto<VendorAttachments>>(
      this.commonBaseURL + "/AddAttachment",
      payload
    );
  }

  updateVendorSummaryCsv(payload: {
    vendorAccount: string;
    vendorProfile: string;
  }): Observable<GenericResponseDto<Vendor>> {
    return this._http.post<GenericResponseDto<Vendor>>(
      this.vendorManagementBaseURL + `/UpdateVendorProfileByVendorAccount`,
      payload
    );
  }

  UpdateVendorSummaryData(
    payload: BillingRepresentative
  ): Observable<GenericResponseDto<BillingRepresentative>> {
    return this._http.post<GenericResponseDto<BillingRepresentative>>(
      this.vendorManagementBaseURL + "/AddOrUpdateBillingRepresentative",
      payload
    );
  }

  UpdateVendorCommentStatus(
    payload: VendorFeedback
  ): Observable<GenericResponseDto<VendorFeedback>> {
    return this._http.post<GenericResponseDto<VendorFeedback>>(
      this.vendorManagementBaseURL + "/UpdateVendorFeedback",
      payload
    );
  }

  getVendorTransactionCount(
    vendorAccountNumber: string
  ): Observable<GenericResponseDto<VendorTransactionCountDto>> {
    return this._http.get<GenericResponseDto<VendorTransactionCountDto>>(
      this.vendorManagementBaseURL +
        `/GetVendorTransactionCount?vendorAccountNumber=${vendorAccountNumber}`
    );
  }

  getAllOpenPO(
    vendorAccountNumber: string
  ): Observable<GenericResponseDto<OpenPOResponseDto[]>> {
    return this._http.get<GenericResponseDto<OpenPOResponseDto[]>>(
      this.vendorManagementBaseURL +
        `/GetAllOpenPO?vendorAccountNumber=${vendorAccountNumber}`
    );
  }

  getAllOpenPOLines(
    vendorAccountNumber: string
  ): Observable<GenericResponseDto<OpenPOLinesResponseDto[]>> {
    return this._http.get<GenericResponseDto<OpenPOLinesResponseDto[]>>(
      this.vendorManagementBaseURL +
        `/GetAllOpenPOLines?vendorAccountNumber=${vendorAccountNumber}`
    );
  }

  uploadW9FileOnD365(
    payload: CreateAttachmentRequestDto
  ): Observable<GenericResponseDto<CreateAttachmentResponseDto>> {
    return this._http.post<GenericResponseDto<CreateAttachmentResponseDto>>(
      this.d365SynchroniserBaseURL + "/CreateAttachment",
      payload
    );
  }

  addContactInformationData(
    payload: AddContactsDto
  ): Observable<
    GenericResponseDto<AddCustomerContactInformationResponseDto[]>
  > {
    return this._http.post<
      GenericResponseDto<AddCustomerContactInformationResponseDto[]>
    >(this.vendorManagementBaseURL + "/AddVendorContact", payload);
  }

  updateContactInformationData(
    payload: AddContactsDto
  ): Observable<
    GenericResponseDto<UpdateCustomerContactInformationResponseDto[]>
  > {
    return this._http.post<
      GenericResponseDto<UpdateCustomerContactInformationResponseDto[]>
    >(this.vendorManagementBaseURL + "/UpdateVendorContact", payload);
  }

  addAddressInformationData(
    payload: AddVendorAddressRequestDto
  ): Observable<GenericResponseDto<AddVendorAddressResponseDto>> {
    return this._http.post<GenericResponseDto<AddVendorAddressResponseDto>>(
      this.vendorManagementBaseURL + "/AddVendorAddress",
      payload
    );
  }

  updateAddressInformationData(
    payload: AddVendorAddressRequestDto
  ): Observable<GenericResponseDto<AddVendorAddressResponseDto>> {
    return this._http.post<GenericResponseDto<AddVendorAddressResponseDto>>(
      this.vendorManagementBaseURL + "/UpdateVendorAddress",
      payload
    );
  }
}
